import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { ChevronRight, ExternalLink, Loader2, Search, UserRound, X } from 'lucide-react';
import { useEffect, useState } from 'react';

import type { ClientBuyFilter, ClientOriginFilter, PartnerClient } from '@/api/types';
import { Skeleton, SkeletonCard, SkeletonLine } from '@/components/ui/skeleton';
import { SegmentControl } from '@/components/ui/segment-control';
import { api } from '@/lib/api';
import { originLabel } from '@/lib/channel';
import { getWebApp, haptic } from '@/lib/telegram';
import { ClientsSwitch } from '@/components/SectionSwitch';

/**
 * Все клиенты ботов партнёра, а не только купившие.
 *
 * До 24.09.2026 человека было видно только через его подписку: кто зарегистрировался
 * на сайте и ничего не купил, не был виден нигде. Поиск идёт и по почте: у клиента с
 * сайта нет Telegram.
 */
export const Route = createFileRoute('/clients')({ component: Clients });

const SEARCH_DEBOUNCE_MS = 300;

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Вторая строка под именем: то, по чему человека можно найти и узнать. */
function secondary(c: PartnerClient): string | null {
  if (c.tgUserId != null) return `tg id ${c.tgUserId}`;
  if (c.email && c.email !== c.name) return c.email;
  if (c.externalId && c.externalId !== c.name) return c.externalId;
  return null;
}

function Clients() {
  const navigate = useNavigate();
  const [origin, setOrigin] = useState<ClientOriginFilter>('all');
  const [buy, setBuy] = useState<ClientBuyFilter>('all');
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const id = setTimeout(() => setDebounced(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [search]);

  const list = useInfiniteQuery({
    queryKey: ['partner-clients', origin, buy, debounced],
    queryFn: ({ pageParam }) => api.clients({ origin, buy, q: debounced, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const { data: counts } = useQuery({
    queryKey: ['partner-clients-counts', debounced],
    queryFn: () => api.clientCounts({ q: debounced }),
  });

  useEffect(() => {
    const tg = getWebApp();
    // Корень вкладки «Клиенты»: назад здесь некуда.
    tg?.BackButton.hide();
    tg?.MainButton.hide();
  }, []);

  const rows = list.data?.pages.flatMap((p) => p.items) ?? [];
  const withCount = (label: string, n: number | undefined) => (n === undefined ? label : `${label} ${n}`);
  const origins: { value: ClientOriginFilter; label: string }[] = [
    { value: 'all', label: withCount('Все', counts?.total) },
    { value: 'telegram', label: withCount('Telegram', counts?.telegram) },
    { value: 'web', label: withCount('Сайт', counts?.web) },
  ];
  if ((counts?.api ?? 0) > 0) origins.push({ value: 'api', label: withCount('API', counts?.api) });

  const open = (c: PartnerClient) => {
    if (c.subscriptionsTotal === 0) return;
    haptic('light');
    navigate({ to: '/subscriptions', search: { client: c.id, clientName: c.name } });
  };

  return (
    <div className="p-4 space-y-4 pb-28">
      <ClientsSwitch />
      <div className="px-1 pt-2">
        <h2 className="font-display text-2xl font-bold tracking-tight mb-3">Клиенты</h2>
        <div className="relative mb-2.5">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dim pointer-events-none" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Поиск: почта, @username, tg id"
            className="w-full surface-inset rounded-full pl-9 pr-9 py-2.5 text-sm outline-none placeholder:text-dim"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-dim"
              aria-label="Очистить поиск"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className="mb-2.5">
          <SegmentControl<ClientOriginFilter> value={origin} onChange={setOrigin} options={origins} />
        </div>
        <SegmentControl<ClientBuyFilter>
          value={buy}
          onChange={setBuy}
          options={[
            { value: 'all', label: 'Любые' },
            { value: 'active', label: withCount('С подпиской', counts?.withActive) },
            { value: 'never', label: withCount('Без покупок', counts?.neverBought) },
          ]}
        />
      </div>

      {/* Панель управления клиентом (дни, тариф, устройства, ссылка, история) пока
          только в карточке на сайте: мини-апп кабинета переводится второй волной. */}
      <button
        type="button"
        onClick={() => {
          haptic('light');
          const url = 'https://hexpartners.pro/vpn/app/clients';
          const tg = getWebApp();
          if (tg?.openLink) tg.openLink(url);
          else window.open(url, '_blank', 'noopener');
        }}
        className="w-full text-left flex items-center gap-3 p-3.5 rounded-[14px] bg-white/[0.07] border border-white/[0.12] active:scale-[0.99] transition-transform"
      >
        <ExternalLink className="w-[17px] h-[17px] text-primary-light shrink-0" strokeWidth={1.8} />
        <span className="text-[12.5px] text-muted-foreground">
          Выдать подписку, добавить дни, повысить тариф, отвязать устройства и посмотреть историю
          клиента можно в его карточке на сайте кабинета.
        </span>
      </button>

      <div className="space-y-[11px]">
        {list.isPending ? (
          <ClientsSkeleton />
        ) : list.isError && rows.length === 0 ? (
          <div className="surface-card rounded-[22px] py-8 px-5 text-center">
            <div className="font-display font-semibold text-[15px] text-muted-foreground">
              Не удалось загрузить клиентов
            </div>
            <button
              type="button"
              onClick={() => void list.refetch()}
              className="mt-3 text-[13.5px] font-semibold text-primary-light"
            >
              Повторить
            </button>
          </div>
        ) : rows.length === 0 ? (
          <div className="surface-card rounded-[22px] py-10 px-5 text-center">
            <div className="w-[52px] h-[52px] rounded-[16px] bg-white/[0.05] flex items-center justify-center mx-auto mb-3.5 text-[#6b6d75]">
              <UserRound className="w-[26px] h-[26px]" strokeWidth={1.6} />
            </div>
            <div className="font-display font-semibold text-[15px] text-muted-foreground">
              {(counts?.total ?? 0) === 0 ? 'Клиентов пока нет' : 'По этому фильтру никого нет'}
            </div>
            <div className="text-[12.5px] text-dim mt-1.5">
              {(counts?.total ?? 0) === 0
                ? 'Они появятся здесь, когда люди запустят бота или зарегистрируются на сайте.'
                : 'Сбросьте фильтры или измените поиск.'}
            </div>
          </div>
        ) : (
          rows.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => open(c)}
              disabled={c.subscriptionsTotal === 0}
              className="surface-card w-full rounded-[20px] p-[15px] text-left disabled:cursor-default"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-bold text-[15px] truncate">{c.name}</div>
                  {secondary(c) ? (
                    <div className="font-mono text-[11px] text-dim truncate mt-0.5">{secondary(c)}</div>
                  ) : null}
                </div>
                {c.subscriptionsTotal > 0 ? <ChevronRight className="w-4 h-4 text-dim shrink-0 mt-1" /> : null}
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] text-dim">
                {originLabel(c.origin) ? (
                  <span
                    className={
                      c.origin === 'web'
                        ? 'rounded-full bg-white/[0.07] px-2.5 py-0.5 font-semibold text-primary-light'
                        : 'rounded-full bg-white/[0.06] px-2.5 py-0.5 font-semibold'
                    }
                  >
                    {originLabel(c.origin)}
                  </span>
                ) : null}
                {c.isBlocked ? (
                  <span className="rounded-full bg-[rgba(248,113,113,0.14)] px-2.5 py-0.5 font-semibold text-[#f87171]">
                    заблокировал бота
                  </span>
                ) : null}
                <span>
                  {c.subscriptionsTotal === 0
                    ? 'нет покупок'
                    : `подписок: ${c.activeSubscriptions} активн. из ${c.subscriptionsTotal}`}
                </span>
                <span className="ml-auto nums">{fmtDate(c.createdAt)}</span>
              </div>
            </button>
          ))
        )}

        {list.hasNextPage && (
          <button
            type="button"
            onClick={() => {
              haptic('light');
              void list.fetchNextPage();
            }}
            disabled={list.isFetchingNextPage}
            className="w-full surface-card rounded-[18px] py-3.5 text-[13.5px] font-semibold text-primary-light disabled:opacity-60"
          >
            {list.isFetchingNextPage ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Показать ещё'}
          </button>
        )}
      </div>
    </div>
  );
}

/** Карточка клиента: имя, строка под ним, строка с откуда пришёл и подписками. */
function ClientsSkeleton() {
  return (
    <>
      {[0, 1, 2].map((i) => (
        <SkeletonCard key={i} className="rounded-[20px] p-[15px]">
          <div className="space-y-2">
            <SkeletonLine size="lg" className="w-2/5" />
            <SkeletonLine size="xs" className="w-1/3" />
          </div>
          <div className="mt-3 flex items-center gap-2">
            <Skeleton className="h-[20px] w-16 rounded-full" />
            <SkeletonLine size="xs" className="w-24" />
          </div>
        </SkeletonCard>
      ))}
    </>
  );
}
